import { useEffect, useRef, useState, useCallback } from 'react';
import { Clock, CheckCircle, AlarmClock, X, Plus, Loader2 } from 'lucide-react';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { formatDate } from '@/lib/dateFormat';
import { DateTimeInput } from '@/components/ui/DateTimeInput';

// ────────────────────────────────────────────────────────────────────────────
// Super-admin personal reminders — the header clock, mirroring the tenant-side
// ReminderBell. Polls active + due reminders every 60s, dings once per newly
// due reminder, and offers a quick "+ Add" form (super-admin has no lead pages
// to create reminders from). Backed by /super-admin/reminders*.
// ────────────────────────────────────────────────────────────────────────────

interface Reminder {
  id: string;
  title: string;
  notes: string | null;
  dueAt: string;
  status: string;
}

const POLL_MS = 60_000;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const diff = d.getTime() - Date.now();
  if (diff < 0) {
    const mins = Math.floor(-diff / 60000);
    if (mins < 60) return `${mins}m overdue`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h overdue`;
    return formatDate(d);
  }
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `in ${mins}m`;
  if (mins < 1440) return `in ${Math.floor(mins / 60)}h`;
  return formatDate(d);
}

// Web Audio ding — same soft chime the tenant app uses.
function playDing() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.4);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch {
    /* ignore */
  }
}

// A Date → the value a <input type="datetime-local"> expects (local time).
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function SuperAdminRemindersBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Reminder[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [dueAt, setDueAt] = useState(() => toLocalInput(new Date(Date.now() + 60 * 60_000)));
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const seenIds = useRef<Set<string>>(new Set());

  const fetchAll = useCallback(async () => {
    try {
      const [dueRes, listRes] = await Promise.all([
        saApi.get('/super-admin/reminders/due'),
        saApi.get('/super-admin/reminders'),
      ]);
      const due: Reminder[] = dueRes.data ?? [];
      setItems(listRes.data ?? []);
      const fresh = due.filter(r => !seenIds.current.has(r.id));
      if (fresh.length > 0) {
        playDing();
        fresh.forEach(r => seenIds.current.add(r.id));
      }
    } catch {
      /* silent — best-effort */
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, POLL_MS);
    return () => clearInterval(t);
  }, [fetchAll]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const addReminder = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await saApi.post('/super-admin/reminders', {
        title: title.trim(),
        dueAt: new Date(dueAt).toISOString(),
      });
      setTitle('');
      setDueAt(toLocalInput(new Date(Date.now() + 60 * 60_000)));
      setShowAdd(false);
      fetchAll();
    } catch {
      /* silent */
    } finally {
      setSaving(false);
    }
  };

  const snooze = async (id: string, minutes: number) => {
    seenIds.current.delete(id);
    setItems(prev => prev.filter(r => r.id !== id));
    try {
      await saApi.patch(`/super-admin/reminders/${id}/snooze`, { minutes });
    } finally {
      fetchAll();
    }
  };

  const act = async (id: string, path: 'done' | 'dismiss') => {
    setItems(prev => prev.filter(r => r.id !== id));
    try {
      await saApi.patch(`/super-admin/reminders/${id}/${path}`);
    } catch {
      fetchAll();
    }
  };

  const count = items.length;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        title="Reminders"
        className="relative text-[#64748B] hover:text-[#6366F1] transition-colors p-1.5 rounded-lg hover:bg-gray-50"
      >
        <Clock className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[70vh] bg-white rounded-xl border border-slate-200 shadow-2xl z-50 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#6366F1]" />
              <span className="text-sm font-bold text-slate-800">Reminders</span>
              {count > 0 && (
                <span className="bg-[#6366F1] text-white text-[10px] px-1.5 py-0.5 rounded-full font-semibold">{count}</span>
              )}
            </div>
            <button
              onClick={() => setShowAdd(v => !v)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#6366F1] hover:underline"
            >
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>

          {showAdd && (
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 space-y-2">
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Remind me to…"
                className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
              />
              <DateTimeInput mode="datetime" inputClassName="h-9" value={dueAt} onChange={setDueAt} />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowAdd(false)} className="px-3 h-8 rounded-lg border border-slate-200 hover:bg-slate-100 text-xs">Cancel</button>
                <button
                  onClick={addReminder}
                  disabled={saving || !title.trim()}
                  className="px-3 h-8 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-xs font-semibold inline-flex items-center gap-1.5"
                >
                  {saving && <Loader2 className="h-3 w-3 animate-spin" />} Save
                </button>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-10 text-center">
                <Clock className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No upcoming reminders</p>
              </div>
            ) : (
              items.map(r => {
                const overdue = new Date(r.dueAt) <= new Date();
                return (
                  <div key={r.id} className="px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{r.title}</p>
                        {r.notes && <p className="text-xs text-slate-500 truncate mt-0.5">{r.notes}</p>}
                        <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${overdue ? 'text-red-500' : 'text-slate-500'}`}>
                          <Clock className="h-3 w-3" /> {formatWhen(r.dueAt)}
                        </div>
                      </div>
                      <button onClick={() => act(r.id, 'dismiss')} className="text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0 mt-0.5">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => snooze(r.id, 15)} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-slate-600 text-xs hover:bg-slate-200 transition-colors">
                        <AlarmClock className="h-3 w-3" /> 15m
                      </button>
                      <button onClick={() => snooze(r.id, 60)} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-slate-600 text-xs hover:bg-slate-200 transition-colors">
                        <AlarmClock className="h-3 w-3" /> 1h
                      </button>
                      <button onClick={() => act(r.id, 'done')} className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-600 text-xs hover:bg-emerald-100 transition-colors">
                        <CheckCircle className="h-3 w-3" /> Done
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
